import { useTranslation } from 'react-i18next';
import { Alert, Card, Skeleton, Space, Switch, Typography } from 'antd';
import { BellRing } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { notificationPreferencesApi } from '@api/notificationPreferences';
import { resolveApiErrorMessage } from '@/i18n/apiError';

const { Paragraph, Text } = Typography;

/**
 * REQ-42 / MOD-13 — self-service iş akışı e-posta bildirim tercihi.
 *
 * Uygulama içi bildirimler her zaman gönderilir; bu toggle yalnız e-posta
 * kanalını (onaya gönderildi/onaylandı/reddedildi/yayınlandı) açıp kapatır.
 * GET/PUT `/notifications/preferences` — RBAC izni yok, çağıranın kendi tercihi.
 */
export function NotificationPreferencesCard() {
  const { t } = useTranslation(['me-preferences', 'common']);
  const qc = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['notification-preferences'],
    queryFn: () => notificationPreferencesApi.get(),
  });

  const mutation = useMutation({
    mutationFn: (emailNotificationsEnabled: boolean) =>
      notificationPreferencesApi.update({ emailNotificationsEnabled }),
    onSuccess: (result) => {
      qc.setQueryData(['notification-preferences'], result);
      toast.success(
        result.emailNotificationsEnabled ? t('email.toastEnabled') : t('email.toastDisabled'),
      );
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  return (
    <Card
      title={
        <Space>
          <BellRing size={18} />
          {t('email.title')}
        </Space>
      }
    >
      {isError ? (
        <Alert type="error" showIcon message={t('alert.loadFailed')} />
      ) : isLoading ? (
        <Skeleton active paragraph={{ rows: 2 }} />
      ) : (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            {t('email.description')}
          </Paragraph>
          <Space size={12} align="center">
            <Switch
              checked={data?.emailNotificationsEnabled ?? true}
              loading={mutation.isPending}
              onChange={(checked) => mutation.mutate(checked)}
              aria-label={t('email.switchLabel')}
            />
            <Text strong>{t('email.switchLabel')}</Text>
            <Text type="secondary">
              {(data?.emailNotificationsEnabled ?? true)
                ? t('email.enabled')
                : t('email.disabled')}
            </Text>
          </Space>
        </Space>
      )}
    </Card>
  );
}
